/* @flow */
import humanparser from 'humanparser';
import * as React from 'react';

import AuthActions from 'auth/AuthActions';
import Button from 'components/Button.jsx';
import createChainedFunction from 'utils/createChainedFunction';
import Device from 'entities/Device';
import DividerText from 'components/DividerText.jsx';
import EventTracking from 'lib/EventTracking';
import Form from 'components/Form.jsx';
import FormInput from 'components/FormInput.jsx';
import GoogleSignIn from 'auth/components/GoogleSignIn.jsx';
import TosText from 'components/TosText.jsx';
import UserActions from 'user/UserActions';
import errorLogger from 'utils/errorLogger';

import type FormInvalidator from 'lib/Formsy';

import 'styles/utilities.scss';

type Props = {
  onUserCreate: Function,
  defaultValues?: ?Object,
  role?: ?string,
};

class JoinSignUpForm extends React.Component<Props, $FlowFixMeState> {
  static defaultProps = {
    defaultValues: {},
    role: '',
  };

  state = {
    canSubmit: false,
  };

  triggerTosViewEvent = () => {
    EventTracking.trackKey('tos_and_pp.view');
  };

  onSubmit = (
    attrs: { email: string, password: string, full_name: string },
    _: any,
    invalidateForm: FormInvalidator
  ) => {
    const doLogin = () => {
      const onSignInSuccess = () => {
        this.props.onUserCreate();
      };

      const onSignInFailure = () => {
        invalidateForm({ email: 'Email has already been taken' });
      };

      AuthActions.signIn(attrs.email, attrs.password)
        .then(onSignInSuccess, onSignInFailure)
        .catch(errorLogger('JoinSignUpForm signIn failed'));
    };

    const doSignup = () => {
      const human = humanparser.parseName(attrs['full_name']);
      const userData = {
        email: attrs.email,
        prefix: human.salutation,
        password: attrs.password,
        last_name: human.lastName,
        first_name: human.firstName,
        role: this.props.role /* !!DO NOT REMOVE THIS!!! otherwise it will default to teacher */,
      };

      if (!userData['last_name']) {
        invalidateForm({ full_name: 'Name must include first and last names' });
        return;
      }

      const onSuccess = () => {
        this.props.onUserCreate();
        this.triggerTosViewEvent();
      };

      const onError = error => {
        const errors = {};

        if (error.code === 'email_used_by_google_sign_in') {
          invalidateForm({ email: error.error });
          return;
        }

        for (const field of error.field_errors) {
          errors[field['field_name']] = field.messages.join(', ');
        }

        invalidateForm(errors);
      };

      UserActions.create(userData)
        .then(onSuccess, onError)
        .catch(errorLogger('JoinSignUpForm user create failed'));
    };

    Device.belongsToFullAccount('email://' + attrs.email)
      .then(doLogin, doSignup)
      .catch(errorLogger('JoinSignUpForm submit failed'));
  };

  render() {
    return (
      <div>
        <Form
          onSubmit={this.onSubmit}
          onValid={() => this.setState({ canSubmit: true })}
          onInvalid={() => this.setState({ canSubmit: false })}>
          <FormInput
            label="Full name"
            type="text"
            size="large"
            name="full_name"
            autoComplete="name"
            placeholder="First and last name"
            validations="minLength:3"
            // $FlowIssue
            value={this.props.defaultValues['full_name']}
            showErrorAfterBlur
            className="JoinSignUpForm-Name"
            validationErrors={{
              minLength: 'Names should be at least 3 characters long.',
            }}
          />
          <FormInput
            label="Email"
            type="email"
            name="email"
            // even though this is an email, let the browser know to use this as username in the future
            autoComplete="username"
            size="large"
            // $FlowIssue
            value={this.props.defaultValues['email']}
            placeholder={'name@example.com'}
            showErrorAfterBlur
            validations="minLength:1"
            className="JoinSignUpForm-Email"
            validationErrors={{
              minLength: 'Invalid, must not be empty',
            }}
          />
          <FormInput
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            size="large"
            placeholder="New password"
            showErrorAfterBlur
            validations="minLength:6"
            className="JoinSignUpForm-Password"
            validationErrors={{
              minLength: 'Passwords should be at least 6 characters long.',
            }}
          />
          <Button
            type="submit"
            level="primary"
            full
            className="JoinSignUpForm-Submit"
            disabled={!this.state.canSubmit}
            eventElementName="JoinSignUpForm-Submit">
            Sign up
          </Button>
        </Form>

        <DividerText uppercase>or</DividerText>

        <div className="block">
          <GoogleSignIn
            body="Sign up with Google"
            onSuccess={createChainedFunction(this.props.onUserCreate, this.triggerTosViewEvent)}
          />
        </div>

        <TosText className="JoinSignUpForm-tos" />
      </div>
    );
  }
}

export default JoinSignUpForm;
